import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Input, InputNumber, Switch, message, Popconfirm, Tooltip, Progress, Select, Empty, Descriptions, Statistic, Row, Col, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, KeyOutlined, ArrowLeftOutlined, ReloadOutlined, UserOutlined, CrownOutlined, CheckCircleOutlined, StopOutlined, CalculatorOutlined, BarChartOutlined } from '@ant-design/icons';
import { adminApi } from '../../services/api';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { adminPath } from '../../../shared/config/paths';
import { formatBytes } from '../../../shared/utils/format';

const Users = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const domainIdFromUrl = searchParams.get('domain_id');
    const clusterIdFromUrl = searchParams.get('cluster_id');
    const domainNameFromUrl = searchParams.get('domain_name');

    const [users, setUsers] = useState([]);
    const [domains, setDomains] = useState([]);
    const [clusters, setClusters] = useState([]);
    const [selectedCluster, setSelectedCluster] = useState(clusterIdFromUrl ? Number(clusterIdFromUrl) : null);
    const [selectedDomain, setSelectedDomain] = useState(domainIdFromUrl ? Number(domainIdFromUrl) : null);
    const [selectedDomainName, setSelectedDomainName] = useState(domainNameFromUrl || '');
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [passwordModalVisible, setPasswordModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [batchLoading, setBatchLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [passwordSubmitting, setPasswordSubmitting] = useState(false);
    const [form] = Form.useForm();
    const [passwordForm] = Form.useForm();

    // User stats states
    const [statsModalVisible, setStatsModalVisible] = useState(false);
    const [statsUser, setStatsUser] = useState(null);
    const [userStats, setUserStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);

    const fetchClusters = async () => {
        try {
            const res = await adminApi.get('/webmail/clusters');
            if (res.data.code === 0) {
                const list = res.data.data.list || [];
                setClusters(list);
                if (!selectedCluster) {
                    const defaultCluster = list.find(c => c.is_default);
                    if (defaultCluster) {
                        setSelectedCluster(defaultCluster.id);
                    } else if (list.length > 0) {
                        setSelectedCluster(list[0].id);
                    }
                }
            }
        } catch (error) {
            message.error('获取集群列表失败');
        }
    };

    const fetchDomains = async () => {
        if (!selectedCluster) return;
        try {
            const res = await adminApi.get('/webmail/domains', {
                params: { cluster_id: selectedCluster, per_page: 100 }
            });
            if (res.data.code === 0) {
                setDomains(res.data.data.list || []);
            }
        } catch (error) {
            message.error('获取域名列表失败');
        }
    };

    const fetchUsers = async (page = 1, pageSize = 20) => {
        if (!selectedCluster || !selectedDomain) return;
        setSelectedRowKeys([]);
        setLoading(true);
        try {
            const res = await adminApi.get('/webmail/users', {
                params: { cluster_id: selectedCluster, domain_id: selectedDomain, page, per_page: pageSize }
            });
            if (res.data.code === 0) {
                setUsers(res.data.data.list || []);
                setPagination({
                    current: res.data.data.page,
                    pageSize: res.data.data.per_page,
                    total: res.data.data.total,
                });
            }
        } catch (error) {
            message.error('获取用户列表失败');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchClusters();
    }, []);

    useEffect(() => {
        if (selectedCluster) {
            fetchDomains();
        }
    }, [selectedCluster]);

    useEffect(() => {
        if (selectedCluster && selectedDomain) {
            fetchUsers();
        }
    }, [selectedCluster, selectedDomain]);

    const handleDomainChange = (domainId) => {
        setSelectedDomain(domainId);
        const domain = domains.find(d => d.id === domainId);
        setSelectedDomainName(domain?.name || '');
    };

    const handleAdd = () => {
        setEditingUser(null);
        form.resetFields();
        form.setFieldsValue({
            active: true,
            admin: false,
            quota_bytes_gb: 1,
        });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingUser(record);
        form.setFieldsValue({
            ...record,
            quota_bytes_gb: record.quota_bytes / (1024 * 1024 * 1024),
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const res = await adminApi.delete(`/webmail/users/${id}`, {
                params: { cluster_id: selectedCluster }
            });
            if (res.data.code === 0) {
                message.success('删除成功');
                fetchUsers(pagination.current, pagination.pageSize);
            } else {
                message.error(res.data.message || '删除失败');
            }
        } catch (error) {
            message.error('删除失败');
        }
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const data = {
                ...values,
                cluster_id: selectedCluster,
                domain_id: selectedDomain,
                quota_bytes: values.quota_bytes_gb ? values.quota_bytes_gb * 1024 * 1024 * 1024 : values.quota_bytes,
            };
            delete data.quota_bytes_gb;

            if (editingUser) {
                delete data.username;
                const res = await adminApi.put(`/webmail/users/${editingUser.id}`, data);
                if (res.data.code === 0) {
                    message.success('更新成功');
                    setModalVisible(false);
                    fetchUsers(pagination.current, pagination.pageSize);
                } else {
                    message.error(res.data.message || '更新失败');
                }
            } else {
                const res = await adminApi.post('/webmail/users', data);
                if (res.data.code === 0) {
                    message.success(`创建成功，邮箱: ${res.data.data.email}`);
                    setModalVisible(false);
                    fetchUsers(pagination.current, pagination.pageSize);
                } else {
                    message.error(res.data.message || '创建失败');
                }
            }
        } catch (error) {
            message.error('操作失败');
        } finally {
            setSubmitting(false);
        }
    };

    const handleResetPassword = (record) => {
        setEditingUser(record);
        passwordForm.resetFields();
        setPasswordModalVisible(true);
    };

    const handlePasswordSubmit = async (values) => {
        setPasswordSubmitting(true);
        try {
            const res = await adminApi.post(`/webmail/users/${editingUser.id}/reset-password`, {
                cluster_id: selectedCluster,
                password: values.password,
            });
            if (res.data.code === 0) {
                message.success('密码已重置');
                setPasswordModalVisible(false);
            } else {
                message.error(res.data.message || '重置失败');
            }
        } catch (error) {
            message.error('重置失败');
        } finally {
            setPasswordSubmitting(false);
        }
    };

    const handleBatchActive = async (active) => {
        if (selectedRowKeys.length === 0) return;
        setBatchLoading(true);
        try {
            const res = await adminApi.post('/webmail/users/batch/active', {
                cluster_id: selectedCluster,
                user_ids: selectedRowKeys,
                active,
            });
            if (res.data.code === 0) {
                message.success(active ? '已批量启用' : '已批量禁用');
                setSelectedRowKeys([]);
                fetchUsers(pagination.current, pagination.pageSize);
            } else {
                message.error(res.data.message || '操作失败');
            }
        } catch (error) {
            message.error('操作失败');
        }
        setBatchLoading(false);
    };

    const handleBatchDelete = async () => {
        if (selectedRowKeys.length === 0) return;
        setBatchLoading(true);
        try {
            const res = await adminApi.post('/webmail/users/batch/delete', {
                cluster_id: selectedCluster,
                user_ids: selectedRowKeys,
            });
            if (res.data.code === 0) {
                message.success('已批量删除');
                setSelectedRowKeys([]);
                fetchUsers(pagination.current, pagination.pageSize);
            } else {
                message.error(res.data.message || '删除失败');
            }
        } catch (error) {
            message.error('删除失败');
        }
        setBatchLoading(false);
    };

    const handleRecalculate = async (record) => {
        try {
            const res = await adminApi.post(`/webmail/users/${record.id}/recalculate`, {
                cluster_id: selectedCluster,
            });
            if (res.data.code === 0) {
                message.success('用量已重新计算');
                fetchUsers(pagination.current, pagination.pageSize);
            } else {
                message.error(res.data.message || '重算失败');
            }
        } catch (error) {
            message.error('重算失败');
        }
    };

    const handleToggleStatus = async (record) => {
        try {
            const res = await adminApi.post(`/webmail/users/${record.id}/toggle-status`, {
                cluster_id: selectedCluster,
            });
            if (res.data.code === 0) {
                message.success(res.data.message);
                fetchUsers(pagination.current, pagination.pageSize);
            } else {
                message.error(res.data.message || '操作失败');
            }
        } catch (error) {
            message.error('操作失败');
        }
    };

    const handleViewStats = async (record) => {
        setStatsUser(record);
        setUserStats(null);
        setStatsModalVisible(true);
        setStatsLoading(true);
        try {
            const res = await adminApi.get(`/webmail/users/${record.id}/stats`, {
                params: { cluster_id: selectedCluster }
            });
            if (res.data.code === 0) {
                setUserStats(res.data.data || {});
            } else {
                message.error(res.data.message || '获取统计失败');
            }
        } catch (error) {
            message.error('获取统计失败');
        }
        setStatsLoading(false);
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 60,
        },
        {
            title: '邮箱',
            dataIndex: 'email',
            render: (email, record) => (
                <Space>
                    {record.admin ? <CrownOutlined style={{ color: '#faad14' }} /> : <UserOutlined />}
                    <strong>{email}</strong>
                </Space>
            ),
        },
        {
            title: '显示名',
            dataIndex: 'display_name',
        },
        {
            title: '状态',
            dataIndex: 'active',
            width: 80,
            render: (active, record) => (
                <Switch
                    checked={active}
                    size="small"
                    onChange={() => handleToggleStatus(record)}
                />
            ),
        },
        {
            title: '存储使用',
            width: 150,
            render: (_, record) => (
                <Tooltip title={`${formatBytes(record.used_bytes)} / ${formatBytes(record.quota_bytes)}`}>
                    <Progress
                        percent={Math.round((record.used_bytes / record.quota_bytes) * 100) || 0}
                        size="small"
                        status={record.used_bytes / record.quota_bytes > 0.9 ? 'exception' : 'normal'}
                    />
                </Tooltip>
            ),
        },
        {
            title: '邮件数',
            dataIndex: 'used_messages',
            width: 80,
            render: (count) => count?.toLocaleString() || 0,
        },
        {
            title: '最后登录',
            dataIndex: 'last_login_at',
            width: 160,
            render: (time) => time ? new Date(time).toLocaleString() : '从未登录',
        },
        {
            title: '操作',
            width: 220,
            render: (_, record) => (
                <Space>
                    <Tooltip title="重置密码">
                        <Button size="small" icon={<KeyOutlined />} onClick={() => handleResetPassword(record)} />
                    </Tooltip>
                    <Tooltip title="重算用量">
                        <Button size="small" icon={<CalculatorOutlined />} onClick={() => handleRecalculate(record)} />
                    </Tooltip>
                    <Tooltip title="统计">
                        <Button size="small" icon={<BarChartOutlined />} onClick={() => handleViewStats(record)} />
                    </Tooltip>
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Popconfirm
                        title="确定删除此用户吗？"
                        description="删除后用户的所有邮件将被清除"
                        onConfirm={() => handleDelete(record.id)}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card
            title={
                <Space>
                    {domainIdFromUrl && (
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(adminPath('/webmail/domains'))} />
                    )}
                    <span>邮箱用户管理 {selectedDomainName && `- ${selectedDomainName}`}</span>
                </Space>
            }
            extra={
                <Space>
                    <Select
                        style={{ width: 150 }}
                        value={selectedCluster}
                        onChange={setSelectedCluster}
                        placeholder="选择集群"
                    >
                        {clusters.map(c => (
                            <Select.Option key={c.id} value={c.id}>
                                {c.name} {c.is_default && '(默认)'}
                            </Select.Option>
                        ))}
                    </Select>
                    <Select
                        style={{ width: 200 }}
                        value={selectedDomain}
                        onChange={handleDomainChange}
                        placeholder="选择域名"
                        showSearch
                        optionFilterProp="children"
                    >
                        {domains.map(d => (
                            <Select.Option key={d.id} value={d.id}>
                                {d.name}
                            </Select.Option>
                        ))}
                    </Select>
                    <Button icon={<ReloadOutlined />} onClick={() => fetchUsers(pagination.current, pagination.pageSize)} />
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} disabled={!selectedDomain}>
                        添加用户
                    </Button>
                </Space>
            }
        >
            {selectedRowKeys.length > 0 && (
                <Space style={{ marginBottom: 12 }}>
                    <span>已选 {selectedRowKeys.length} 项</span>
                    <Button
                        size="small"
                        icon={<CheckCircleOutlined />}
                        loading={batchLoading}
                        onClick={() => handleBatchActive(true)}
                    >
                        批量启用
                    </Button>
                    <Button
                        size="small"
                        icon={<StopOutlined />}
                        loading={batchLoading}
                        onClick={() => handleBatchActive(false)}
                    >
                        批量禁用
                    </Button>
                    <Popconfirm
                        title={`确定删除选中的 ${selectedRowKeys.length} 个用户吗？`}
                        description="删除后用户的所有邮件将被清除"
                        onConfirm={handleBatchDelete}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} loading={batchLoading}>
                            批量删除
                        </Button>
                    </Popconfirm>
                </Space>
            )}

            <Table
                columns={columns}
                dataSource={users}
                rowKey="id"
                loading={loading}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: <Empty description={selectedDomain ? '该域名下暂无邮箱用户，点击右上角“添加用户”创建' : '请先选择域名'} /> }}
                rowSelection={{
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys),
                }}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showTotal: (total) => `共 ${total} 条`,
                    onChange: (page, pageSize) => fetchUsers(page, pageSize),
                }}
            />

            <Modal
                title={editingUser ? '编辑用户' : '添加用户'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={500}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    {!editingUser && (
                        <>
                            <Form.Item
                                name="username"
                                label="用户名"
                                rules={[
                                    { required: true, message: '请输入用户名' },
                                    { pattern: /^[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?$/i, message: '只填 @ 前面的部分：字母、数字、. _ -' },
                                ]}
                                normalize={(v) => (v || '').trim().replace(new RegExp(`@${(selectedDomainName || '').replace(/\./g, '\\.')}$`, 'i'), '')}
                            >
                                <Input placeholder="name" addonAfter={selectedDomainName ? `@${selectedDomainName}` : undefined} autoComplete="off" />
                            </Form.Item>
                            <Form.Item
                                name="password"
                                label="密码"
                                rules={[{ required: true, message: '请输入密码' }]}
                            >
                                <Input.Password placeholder="登录密码" />
                            </Form.Item>
                        </>
                    )}
                    <Form.Item name="display_name" label="显示名称">
                        <Input placeholder="用户显示名称" />
                    </Form.Item>
                    <Form.Item name="quota_bytes_gb" label="存储配额 (GB)">
                        <InputNumber min={0.1} max={1000} step={0.1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Space>
                        <Form.Item name="active" valuePropName="checked">
                            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
                        </Form.Item>
                        <Form.Item name="admin" valuePropName="checked">
                            <Switch checkedChildren="管理员" unCheckedChildren="普通用户" />
                        </Form.Item>
                    </Space>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={submitting}>保存</Button>
                            <Button onClick={() => setModalVisible(false)} disabled={submitting}>取消</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`重置密码 - ${editingUser?.email}`}
                open={passwordModalVisible}
                onCancel={() => setPasswordModalVisible(false)}
                footer={null}
                width={400}
            >
                <Form form={passwordForm} layout="vertical" onFinish={handlePasswordSubmit}>
                    <Form.Item
                        name="password"
                        label="新密码"
                        rules={[
                            { required: true, message: '请输入新密码' },
                            { min: 6, message: '密码至少6位' }
                        ]}
                    >
                        <Input.Password placeholder="输入新密码" />
                    </Form.Item>
                    <Form.Item
                        name="confirmPassword"
                        label="确认密码"
                        dependencies={['password']}
                        rules={[
                            { required: true, message: '请确认密码' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('两次输入的密码不一致'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder="再次输入新密码" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={passwordSubmitting}>重置密码</Button>
                            <Button onClick={() => setPasswordModalVisible(false)} disabled={passwordSubmitting}>取消</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* User Stats Modal */}
            <Modal
                title={`邮箱统计 - ${statsUser?.email || ''}`}
                open={statsModalVisible}
                onCancel={() => setStatsModalVisible(false)}
                width={600}
                footer={[
                    <Button key="refresh" icon={<ReloadOutlined />} onClick={() => statsUser && handleViewStats(statsUser)}>
                        刷新
                    </Button>,
                    <Button key="close" onClick={() => setStatsModalVisible(false)}>关闭</Button>,
                ]}
            >
                <Spin spinning={statsLoading}>
                    {userStats ? (
                        <>
                            <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
                                <Col xs={12} sm={8}>
                                    <Card size="small">
                                        <Statistic title="存储使用" value={formatBytes(userStats.used_bytes ?? statsUser?.used_bytes ?? 0)} />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={8}>
                                    <Card size="small">
                                        <Statistic title="邮件总数" value={userStats.used_messages ?? userStats.total_messages ?? statsUser?.used_messages ?? 0} />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={8}>
                                    <Card size="small">
                                        <Statistic title="存储配额" value={formatBytes(userStats.quota_bytes ?? statsUser?.quota_bytes ?? 0)} />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={8}>
                                    <Card size="small">
                                        <Statistic title="今日发送" value={userStats.today_sent ?? 0} />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={8}>
                                    <Card size="small">
                                        <Statistic title="今日接收" value={userStats.today_received ?? 0} />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={8}>
                                    <Card size="small">
                                        <Statistic title="今日失败" value={userStats.today_failed ?? 0} valueStyle={{ color: (userStats.today_failed || 0) > 0 ? '#cf1322' : undefined }} />
                                    </Card>
                                </Col>
                            </Row>
                            <Descriptions bordered column={1} size="small" style={{ marginTop: 12 }}>
                                <Descriptions.Item label="邮箱">{statsUser?.email || '-'}</Descriptions.Item>
                                <Descriptions.Item label="最后登录">{userStats.last_login_at ? new Date(userStats.last_login_at).toLocaleString() : (statsUser?.last_login_at ? new Date(statsUser.last_login_at).toLocaleString() : '从未登录')}</Descriptions.Item>
                                <Descriptions.Item label="登录次数">{userStats.login_count ?? '-'}</Descriptions.Item>
                            </Descriptions>
                        </>
                    ) : (
                        !statsLoading && <Empty description="暂无统计数据" />
                    )}
                </Spin>
            </Modal>
        </Card>
    );
};

export default Users;
